import ModalComponent from 'components/main/modal'
import { ATTR_NAME } from 'constants/attr-name'
import { CLASS_NAME } from 'constants/class-names'
import { EventType, HtmlAttr } from 'constants/enums'
import { ApiService } from 'services/api/api.service'
import { ModelCard } from '..'
import { MAX_VEHICLES } from '../car-comparison/constants'
import { CarComparisonStore } from '../store'

const { trimSelector: className } = CLASS_NAME

/**
 * Trim Selector modal component
 */
export class TrimSelector extends ModalComponent {
    private trimsWrapper: HTMLDivElement
    private compareButton: HTMLButtonElement
    private compareButtonText: HTMLSpanElement
    private image: HTMLImageElement
    private make: HTMLHeadingElement
    private model: HTMLParagraphElement
    private buttonNoMorePickModal: HTMLButtonElement
    private buttonNoMorePickModalClose: HTMLButtonElement

    private checkedItems: number[] = []
    private vehicleIcons: HTMLDivElement[] = []

    private modelCard: ModelCard

    /**
     * Get the checkbox elements ✅
     *
     * @returns {HTMLInputElement[]}
     */
    private get checkboxes(): HTMLInputElement[] {
        return this.getElements('input[type="checkbox"]', this.modal)
    }

    public constructor(id: string) {
        super(undefined, id)

        if (!this.bindHTMLElements()) {
            return
        }

        this.compareButton.addEventListener(EventType.CLICK, () => {
            this.modelCard.disableCompare()
            // Add to MyPicks
            this.checkedItems.forEach(async (vehicleId) =>
                CarComparisonStore.getInstance().add(vehicleId)
            )
            this.checkedItems = []
            this.closeModal()
        })

        this.buttonNoMorePickModal.addEventListener(EventType.CLICK, () => {
            CarComparisonStore.getInstance()
                .getNoMorePickModal()
                .closeModal()
            CarComparisonStore.getInstance()
                .getTrimSelector()
                .openModal()
        })

        this.buttonNoMorePickModalClose.addEventListener(
            EventType.CLICK,
            (e) => {
                e.preventDefault()
                CarComparisonStore.getInstance()
                    .getNoMorePickModal()
                    .closeModal()
                CarComparisonStore.getInstance()
                    .getTrimSelector()
                    .openModal()
            }
        )
    }

    /**
     * Bind HTML Elements
     * @returns {boolean} Indicate if the HTML structure is on the screen
     */
    private bindHTMLElements(): boolean {
        const trimsWrapper = this.getElement<HTMLDivElement>(
            `.${className.wrapper}`,
            this.modal
        )

        const compareButton = this.getElement<HTMLButtonElement>(
            `.${className.button}`,
            this.modal
        )

        const image = this.getElement<HTMLImageElement>(
            `.${className.image}`,
            this.modal
        )

        const make = this.getElement<HTMLHeadingElement>(
            `.${className.make}`,
            this.modal
        )

        const model = this.getElement<HTMLParagraphElement>(
            `.${className.model}`,
            this.modal
        )

        const compareButtonText = this.getElement<HTMLParagraphElement>(
            `.${className.buttonText}`,
            compareButton
        )

        const buttonNoMorePickModal = this.getElement<HTMLButtonElement>(
            `.${CLASS_NAME.modal._} .${className.buttonNoMorePickModal}`,
            document.body
        )

        const buttonNoMorePickModalClose = this.getElement<HTMLButtonElement>(
            `.${className.noMorePickModal} .${CLASS_NAME.modal.closeBtn}`,
            document.body
        )

        if (
            !trimsWrapper ||
            !compareButton ||
            !image ||
            !make ||
            !model ||
            !compareButtonText ||
            !buttonNoMorePickModal ||
            !buttonNoMorePickModalClose
        ) {
            return false
        }

        this.trimsWrapper = trimsWrapper
        this.compareButton = compareButton
        this.image = image
        this.make = make
        this.model = model
        this.compareButtonText = compareButtonText
        this.buttonNoMorePickModal = buttonNoMorePickModal
        this.buttonNoMorePickModalClose = buttonNoMorePickModalClose

        this.vehicleIcons = this.getElements(
            `.${className.vehicleIcon}`,
            this.modal
        )
        return true
    }

    /**
     * Open Trim Selector modal
     * Replace the model information and fetch trims from API
     *
     * @param {string} image Image URL
     * @param {number} year
     * @param {string} make
     * @param {string} model
     */
    public async openTrimSelector(modelCard: ModelCard): Promise<void> {
        this.modelCard = modelCard
        this.checkedItems = []
        const { wrapper, year, make, model } = modelCard

        const cardImage = this.getElement(
            `.${CLASS_NAME.findCompare.cardImage}`,
            wrapper
        )

        // Replace the image
        this.image.src =
            (cardImage && cardImage.getAttribute(HtmlAttr.SOURCE)) || ''
        this.image.alt = `${year} ${make} ${model}`

        // Replace the make
        this.make.innerText = `${year} ${make}`

        // Replace the model
        this.model.innerText = model

        // Call vehicle API to replace checkboxes
        this.trimsWrapper.innerHTML = 'Loading...'
        await new ApiService()
            .fetchDrivingVehicles<Response>({
                year: year.toString(),
                make: make,
                model: model,
                format: 'html'
            })
            .then(async (data: Response) => this.parseVehiclesCheckboxes(data))
            .catch(() => {
                this.trimsWrapper.innerHTML = ''
            })

        this.setVehicleIcons()
        this.openModal()
    }

    /**
     * Parse vehicle select checkboxes from the response ✅
     *
     * @param {Response} data
     * @returns {Promise<void>}
     */
    private async parseVehiclesCheckboxes(data: Response): Promise<void> {
        // Replace the checkbox section
        const parser = new DOMParser()
        const text = await data.text()
        const doc = parser.parseFromString(text, 'text/html')

        const body = this.getElement<HTMLBodyElement>('body', doc)
        if (body) {
            this.trimsWrapper.innerHTML = body.innerHTML
        }

        // Event listener for checkboxes
        this.checkboxes.forEach((checkbox, index) => {
            // Force check the first checkbox
            if (index === 0) {
                this.onCheckboxChecked(checkbox)
            }

            checkbox.addEventListener(EventType.CHANGE, () => {
                if (checkbox.checked) {
                    this.onCheckboxChecked(checkbox)
                } else {
                    this.onCheckboxUnchecked(checkbox)
                }
            })
        })
    }

    /**
     * Callback to checkbox on
     *
     * @param {HTMLInputElement} checkbox ✅
     */
    private onCheckboxChecked(checkbox: HTMLInputElement): void {
        checkbox.checked = true
        checkbox.focus()
        if (
            CarComparisonStore.getInstance().get().length +
                this.checkedItems.length >=
            MAX_VEHICLES
        ) {
            // Over the limit
            this.closeModal()
            CarComparisonStore.getInstance()
                .getNoMorePickModal()
                .openModal()
            checkbox.checked = false
            return
        }

        const vehicleId =
            checkbox.getAttribute(ATTR_NAME.driving.trimSelector.vehicleId) ||
            ''
        this.checkedItems.push(parseInt(vehicleId))
        this.setVehicleIcons()
    }

    /**
     * Callback to checkbox off
     *
     * @param {HTMLInputElement} checkbox ✅
     */
    private onCheckboxUnchecked(checkbox: HTMLInputElement): void {
        checkbox.checked = false
        checkbox.focus()
        const vehicleId =
            checkbox.getAttribute(ATTR_NAME.driving.trimSelector.vehicleId) ||
            ''
        this.checkedItems = this.checkedItems.filter(
            (id) => id !== parseInt(vehicleId)
        )
        this.setVehicleIcons()
    }

    /**
     * Assign CSS class to vehicle icons
     */
    private setVehicleIcons(): void {
        const myPicksLength = CarComparisonStore.getInstance().get().length
        const checkboxLength = this.checkedItems.length
        const myPicksClasses = Array(myPicksLength).fill(
            className.vehicleMyPick
        )
        const checkboxClasses = Array(checkboxLength).fill(
            className.vehicleChecked
        )
        const classes = myPicksClasses.concat(checkboxClasses)

        this.vehicleIcons.forEach((icon, index) => {
            icon.classList.remove(
                className.vehicleMyPick,
                className.vehicleChecked
            )

            if (classes.length > index) {
                icon.classList.add(classes[index])
            }
        })

        this.compareButtonText.innerText = `Add ${checkboxLength} to compare`

        this.compareButton.disabled = false
        if (checkboxLength === 0) {
            this.compareButton.disabled = true
            this.compareButtonText.innerText = 'Nothing to compare'
        }
    }
}
